/** Refresh the cable JWT once it is this close to its `exp` claim. */
export const CABLE_TOKEN_REFRESH_MARGIN_MS = 5 * 60_000;

/**
 * Milliseconds at which the `jid` token carried by a cable URL expires, or
 * `undefined` when the URL carries no readable token.
 */
export function cableTokenExpiresAt(url: string): number | undefined {
  let token: string | null;
  try {
    token = new URL(url).searchParams.get('jid');
  } catch {
    return undefined;
  }
  if (!token) return undefined;

  const payload = token.split('.')[1];
  if (!payload) return undefined;
  try {
    const decoded = JSON.parse(decodeBase64Url(payload)) as unknown;
    if (typeof decoded !== 'object' || decoded === null) return undefined;
    const exp = (decoded as { exp?: unknown }).exp;
    return typeof exp === 'number' && Number.isFinite(exp) ? exp * 1000 : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Whether the cable URL's token should be swapped before the next connect. An
 * unreadable token counts as needing refresh: the server would reject it anyway.
 */
export function cableTokenNeedsRefresh(url: string, now: number, marginMs = CABLE_TOKEN_REFRESH_MARGIN_MS): boolean {
  const expiresAt = cableTokenExpiresAt(url);
  if (expiresAt === undefined) return true;
  return expiresAt - now <= marginMs;
}

/** The subset of an AnyCable `Cable` this module drives. */
export interface LivenessCable {
  readonly state: string;
  readonly monitor?: { reconnectNow(): boolean };
  connect(): Promise<unknown>;
  on(event: 'connect', callback: () => void): () => void;
  on(event: 'close', callback: (event?: unknown) => void): () => void;
}

interface EventTargetLike {
  addEventListener(type: string, listener: EventListener): void;
  removeEventListener(type: string, listener: EventListener): void;
}

interface LivenessDocument extends EventTargetLike {
  readonly visibilityState: DocumentVisibilityState;
}

interface Scheduler {
  setTimeout(handler: () => void, delayMs: number): ReturnType<typeof setTimeout>;
  clearTimeout(handle: ReturnType<typeof setTimeout>): void;
}

export interface KeepCableConnectedOptions {
  readonly cable: LivenessCable;
  /** Delay before reconnect attempt `attempt` (zero-based) after a server-initiated close. */
  readonly reconnectDelay: (attempt: number) => number;
  readonly eventTarget?: EventTargetLike;
  readonly lifecycleDocument?: LivenessDocument;
  readonly resumeSource?: { onResume?: (callback: () => void) => () => void };
  readonly scheduler?: Scheduler;
  readonly log?: (message: string, details?: Record<string, unknown>) => void;
}

/**
 * Keeps a page-lifetime cable from settling into a dead state.
 *
 * AnyCable's monitor retries only the `disconnected` state. A server-initiated
 * close (`token_expired`, `unauthorized`) parks the cable in `closed`, where the
 * monitor never looks again, and nothing else in the page calls `connect()`.
 * This reopens closed cables with backoff and pulls forward any pending retry
 * when the page has fresh evidence it can connect: network back online, tab
 * visible again, machine resumed from sleep.
 *
 * Returns a function that unbinds every listener and timer.
 */
export function keepCableConnected(options: KeepCableConnectedOptions): () => void {
  const { cable, reconnectDelay } = options;
  const scheduler: Scheduler = options.scheduler ?? {
    setTimeout: (handler, delayMs) => globalThis.setTimeout(handler, delayMs),
    clearTimeout: (handle) => globalThis.clearTimeout(handle),
  };
  const log = options.log ?? (() => {});

  let attempts = 0;
  let pendingReconnect: ReturnType<typeof setTimeout> | undefined;

  const cancelPendingReconnect = () => {
    if (pendingReconnect === undefined) return;
    scheduler.clearTimeout(pendingReconnect);
    pendingReconnect = undefined;
  };

  const connectNow = (reason: string) => {
    cancelPendingReconnect();
    log(`connecting (${reason})`, { attempts, state: cable.state });
    cable.connect().catch(() => {
      // A failed open lands in `disconnected`, where the monitor's own backoff takes over.
    });
  };

  const scheduleReconnect = (reason: string) => {
    if (pendingReconnect !== undefined) return;
    const delayMs = Math.max(0, reconnectDelay(attempts));
    attempts += 1;
    log(`reconnecting in ${Math.round(delayMs)}ms (${reason})`, { attempts });
    pendingReconnect = scheduler.setTimeout(() => {
      pendingReconnect = undefined;
      connectNow(reason);
    }, delayMs);
  };

  const ensureConnected = (reason: string) => {
    const state = cable.state;
    // `idle` is a lazy cable nobody has subscribed to yet; connecting it here would be a policy change.
    if (state === 'idle' || state === 'connected' || state === 'connecting') return;
    if (pendingReconnect !== undefined) {
      connectNow(reason);
      return;
    }
    // `disconnected`: the monitor owns a backoff timer; run its attempt now instead of waiting it out.
    if (cable.monitor?.reconnectNow()) {
      log(`monitor reconnect pulled forward (${reason})`, { state });
      return;
    }
    // `closed`: the monitor has given up, so open the transport directly.
    connectNow(reason);
  };

  const unbind: Array<() => void> = [];
  unbind.push(
    cable.on('connect', () => {
      attempts = 0;
      cancelPendingReconnect();
    }),
  );
  unbind.push(
    cable.on('close', (event) => {
      // A close without an event is `cable.disconnect()` from our own code; leave it alone.
      if (!event) return;
      scheduleReconnect(`server close: ${closeReason(event)}`);
    }),
  );

  const handleOnline = () => ensureConnected('online');
  const handleVisibilityChange = () => {
    if (options.lifecycleDocument?.visibilityState === 'visible') ensureConnected('visible');
  };
  options.eventTarget?.addEventListener('online', handleOnline);
  options.lifecycleDocument?.addEventListener('visibilitychange', handleVisibilityChange);
  const unsubscribeResume = options.resumeSource?.onResume?.(() => ensureConnected('resume'));

  return () => {
    cancelPendingReconnect();
    for (const release of unbind) release();
    options.eventTarget?.removeEventListener('online', handleOnline);
    options.lifecycleDocument?.removeEventListener('visibilitychange', handleVisibilityChange);
    unsubscribeResume?.();
  };
}

function closeReason(event: unknown): string {
  if (typeof event === 'object' && event !== null && 'reason' in event) {
    const reason = (event as { reason: unknown }).reason;
    if (typeof reason === 'string') return reason;
  }
  return 'unspecified';
}

function decodeBase64Url(value: string): string {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  return atob(padded);
}
